import { useQuery } from '@tanstack/react-query';
import { keepPreviousData } from '@tanstack/react-query';
import { useAPI } from './useAPIs';
import { buildQueryString, sortingToOrdering } from '../common/TablePanel/tableUtils';

/**
 * Custom hook for managing view queries with common patterns
 * 
 * @param {Object} config - Configuration object
 * @param {string} config.queryKey - Base query key
 * @param {Object} config.mergedFilters - Merged filter object
 * @param {Object} config.pagination - Pagination state
 * @param {Array} [config.sorting] - MRT sorting state array, e.g. [{ id: 'datetime', desc: true }]
 * @param {string} config.dateType - Date type filter
 * @param {Object} config.urlMapping - URL parameter mapping
 * @param {Function} config.createIsoStringWithTimezone - Function to create ISO string
 * @param {string} config.endpoint - API endpoint
 * @returns {Object} Query result and utilities
 */
export const useViewQuery = ({
  queryKey,
  mergedFilters,
  pagination,
  sorting,
  dateType,
  urlMapping,
  createIsoStringWithTimezone,
  endpoint,
}) => {
  const { apiCall } = useAPI();

  const query = useQuery({
    queryKey: [queryKey, mergedFilters, pagination.pageIndex, pagination.pageSize, dateType, sorting],
    queryFn: async () => {
      const params = buildQueryString(mergedFilters, urlMapping, createIsoStringWithTimezone, dateType);
      params.append('page', (pagination.pageIndex + 1).toString());
      params.append('page_size', pagination.pageSize.toString());
      const ordering = sortingToOrdering(sorting);
      if (ordering) params.append('ordering', ordering);
      const response = await apiCall('GET', `${endpoint}?${params.toString()}`);
      return response.data;
    },
    placeholderData: keepPreviousData,
  });

  return query;
};

